/**
 * historyHydrator - converts persisted DB message rows into frontend ConversationTurn objects.
 *
 * Extracted from useChatSession to keep the hook focused on WS lifecycle.
 * This module is pure (no React, no I/O) and independently unit-testable.
 */

import type {
  AiStructuredCardActionState,
  ChatMode,
  ExecutionStatus,
  GeminiModel,
  RagReference,
  RunTranscript,
} from '@protoss/shared';
import { isUserFacingStep, parseAiStructuredPayload } from '@protoss/shared';
import { cancelExecution } from '../../../lib/executionStatusHelpers';
import type { AssistantRun, ConversationTurn, UserTurn } from '../state/chatState';

// The deterministic deal-generation pipeline skills (Generate all / Regenerate estimate /
// Regenerate SOW). These narrate their phases inline and never produce a Q&A-style answer,
// so the synthetic "Confidence: X" evaluate step is suppressed for them (see below).
const GENERATION_FAMILY_SKILL_KEYS = new Set<string>([
  'sales_deal_generation',
  'regenerate_estimate',
  'regenerate_sow',
]);

// ─────────────────────────────────────────────────────────────────────────────
// DB row types (serialised form returned by history endpoints)
// ─────────────────────────────────────────────────────────────────────────────

export type AgentTraceStep = {
  id: string;
  type: string;
  status: 'running' | 'done' | 'failed';
  label?: string;
  detail?: string;
  gaps?: string[];
  error?: string;
  sourceCount?: number;
  citedCount?: number;
  toolCalls?: Array<{
    toolCallId: string;
    toolName: string;
    toolLabel: string;
    inputSummary: string;
    status: 'running' | 'completed' | 'failed';
    resultSummary?: string;
    durationMs?: number;
    startedAt: string;
    completedAt?: string;
    progressText?: string;
  }>;
  // For flat tool_execution type steps persisted without a parentStepId:
  toolCallId?: string;
  toolName?: string;
  inputSummary?: string;
  resultSummary?: string;
  durationMs?: number;
  progressText?: string;
  startedAt?: string;
  completedAt?: string;
};

export type SkillExecution = {
  sequenceNo: number;
  skillKey: string;
  status: string;
  evaluationConfidence?: string | null;
  evaluationReasoningSummary?: string | null;
  evaluationGaps?: string[] | null;
  agentTrace?: {
    steps: AgentTraceStep[];
    startedAt?: string;
    completedAt?: string;
    durationMs?: number;
    executionPlan?: {
      steps: Array<{
        id: string;
        title: string;
        status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
      }>;
    };
  } | null;
};

export type HistoryCitation = {
  title: string;
  artifactType: string;
  externalUrl: string | null;
  providerSlug: string;
  citedExcerpt: string | null;
};

export type HistoryMessage = {
  /** The chat_message UUID - used as the live messageId on rehydrated runs. */
  uuid?: string;
  role: string;
  contentText: string;
  /**
   * Persisted structured payload from chat_message.content_json.
   * Present when the assistant turn was an enrichment/structured response card.
   */
  contentJson?: unknown;
  /** Persisted context from the time the message was executed. */
  contextSnapshotJson?: unknown;
  /**
   * Ordered conversational transcript captured server-side during the run (text /
   * tool / question+answer segments). When present, rehydrated into `run.segments`
   * so the run renders exactly as it was seen live. NULL for legacy rows, user
   * messages, and the generic direct-chat path → falls back to text blob + chain.
   */
  transcriptJson?: RunTranscript | null;
  modelName: string | null;
  chatMode?: string | null;
  messageIndex: number;
  status?: string | null;
  createdAt: string;
  sessionId: number;
  /** Persisted user action on the card - 'accepted' | 'dismissed' | null */
  actionResult?: string | null;
  /** What initiated the run - chat_message.trigger_source (ChatTriggerSource values). */
  triggerSource?: string | null;
  /** Precise origin (page + exact button label) - chat_message.trigger_origin. */
  triggerOrigin?: { page: string; label: string } | null;
  /** Machine-readable error code from translateRunError - set on error turns */
  errorCode?: string | null;
  /** Whether the error is retryable - set on error turns; null for legacy rows */
  retryable?: boolean | null;
  /** Persisted token usage (chat_message.token_usage_input/output) - feeds the
   *  context-usage indicator after a reload. Null for legacy/cancelled rows. */
  tokenUsageInput?: number | null;
  tokenUsageOutput?: number | null;
  /** Peak single-call input tokens (context-window occupancy) - feeds the usage meter. */
  tokenUsageContext?: number | null;
  citations?: HistoryCitation[] | null;
  skillExecutions?: SkillExecution[] | null;
};

// ─────────────────────────────────────────────────────────────────────────────
// Hydration functions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Strip legacy 5-section LLM format from historical message text.
 * Old responses included "**2. Evidence**", "**3. Reasoning Summary**" etc.
 * after the answer. Remove everything from the first section header onwards
 * so only the answer prose is displayed.
 */
export function stripLegacySections(text: string): string {
  const sectionHeaderIdx = text.search(/\n\s*\*\*2\.\s*Evidence/);
  if (sectionHeaderIdx === -1) return text;
  return text.slice(0, sectionHeaderIdx).trimEnd();
}

/**
 * Convert a flat list of DB history message rows into ConversationTurn pairs.
 *
 * @param messages - ordered array of messages (ascending messageIndex)
 * @param primaryScopeUuid - projectUuid or workspaceUuid, injected into UserTurn.context
 */
export function messagesToTurns(
  messages: HistoryMessage[],
  primaryScopeUuid: string
): ConversationTurn[] {
  return messages.flatMap((msg): ConversationTurn[] => {
    if (msg.role === 'user') {
      const turn: UserTurn = {
        kind: 'user',
        id: `hist-${msg.sessionId}-${msg.messageIndex}`,
        prompt: msg.contentText,
        context: { projectUuid: primaryScopeUuid },
        ts: msg.createdAt,
        ...(msg.triggerSource && msg.triggerSource !== 'user_chat'
          ? { triggerSource: msg.triggerSource as UserTurn['triggerSource'] }
          : {}),
        // Precise origin (page + exact button label) for the source pill.
        ...(msg.triggerOrigin ? { origin: msg.triggerOrigin } : {}),
      };
      return [turn];
    }
    if (msg.role === 'assistant') {
      const citations = msg.citations ?? [];
      const citationCount = citations.length;
      // A turn now persists one skill_execution row per skill invoked (sequenceNo
      // order). Flatten ALL of them so later skills' steps are not dropped; the eval
      // (confidence) row uses the last execution that recorded one.
      const skillExecs = [...(msg.skillExecutions ?? [])].sort(
        (a, b) => (a.sequenceNo ?? 0) - (b.sequenceNo ?? 0)
      );
      const evalExec =
        [...skillExecs].reverse().find((se) => se.evaluationConfidence) ??
        skillExecs[skillExecs.length - 1] ??
        null;
      const steps: ExecutionStatus['steps'] = [];

      const isCancelled = msg.status === 'cancelled';
      // Also treat legacy rows where broken code stored 'error' as contentJson instead of status
      const isError = msg.status === 'error' || msg.contentJson === 'error';

      const agentTraceSteps = skillExecs.flatMap((se) => se.agentTrace?.steps ?? []);
      const traceHasRetrieve = agentTraceSteps.some((ts) => ts.type === 'retrieve_context');
      const traceHasEvaluate = agentTraceSteps.some((ts) => ts.id === 'step-evaluate');

      // 0. Prepend plan steps from persisted executionPlan (plan_created events)
      const execPlanSteps = skillExecs.flatMap((se) => se.agentTrace?.executionPlan?.steps ?? []);
      if (execPlanSteps.length > 0) {
        for (const ps of execPlanSteps) {
          // 'pending' at end-of-run means the step was never reached → treat as skipped
          // 'running' at end-of-run means interrupted → treat as completed
          const resolvedStatus: 'completed' | 'failed' | 'skipped' =
            ps.status === 'completed'
              ? 'completed'
              : ps.status === 'failed'
                ? 'failed'
                : ps.status === 'running'
                  ? 'completed'
                  : 'skipped';
          steps.push({
            id: ps.id,
            type: 'plan' as const,
            status: resolvedStatus,
            label: ps.title,
            startedAt: msg.createdAt,
            completedAt: msg.createdAt,
          } as unknown as ExecutionStatus['steps'][number]);
        }
      }

      // 1. Rehydrate from persisted trace steps
      for (const ts of agentTraceSteps) {
        // Skip internal/mechanical pipeline steps (intent/context/policy) so older
        // sessions rehydrate as cleanly as live runs. Unknown ids fail open.
        if (ts.id && !isUserFacingStep(ts.id)) continue;
        if (ts.type === 'tool_execution') {
          // Flat tool_execution step - reconstruct as ToolExecutionStep
          steps.push({
            id: ts.id ?? `hist-trace-${Math.random()}`,
            type: 'tool_execution' as const,
            status:
              ts.status === 'done' ? 'completed' : ts.status === 'failed' ? 'failed' : 'completed',
            label: ts.label ?? 'Tool execution',
            toolCallId: ts.toolCallId ?? ts.id,
            toolName: ts.toolName ?? 'unknown',
            progressText: ts.progressText,
            resultSummary: ts.resultSummary,
            durationMs: ts.durationMs,
            startedAt: ts.startedAt ?? msg.createdAt,
            completedAt: ts.completedAt ?? msg.createdAt,
          } as unknown as ExecutionStatus['steps'][number]);
        } else {
          const step = {
            id: ts.id ?? `hist-trace-${Math.random()}`,
            type: ts.type ?? 'generic_progress',
            status:
              ts.status === 'done' ? 'completed' : ts.status === 'failed' ? 'failed' : 'completed',
            label: ts.label ?? ts.type,
            detail: ts.detail,
            ...(ts.gaps !== undefined && ts.gaps.length > 0 && { gaps: ts.gaps }),
            ...(ts.toolCalls !== undefined &&
              ts.toolCalls.length > 0 && { toolCalls: ts.toolCalls }),
            ...(ts.sourceCount !== undefined && { sourceCount: ts.sourceCount }),
            ...(ts.citedCount !== undefined && { citedCount: ts.citedCount }),
            startedAt: ts.startedAt ?? msg.createdAt,
            completedAt: ts.completedAt ?? msg.createdAt,
          } as unknown as ExecutionStatus['steps'][number];
          steps.push(step);
        }
      }

      // Synthetic steps are only meaningful for completed runs.
      // Cancelled/error runs should not show a misleading "Generated response" step.
      if (!isCancelled && !isError) {
        // 2. Synthetic retrieve_context if no trace-based one but citations exist
        if (!traceHasRetrieve && citationCount > 0) {
          steps.unshift({
            id: `hist-ctx-${msg.sessionId}-${msg.messageIndex}`,
            type: 'retrieve_context' as const,
            status: 'completed' as const,
            label: 'Retrieved context',
            sourceCount: citationCount,
            citedCount: citationCount,
            startedAt: msg.createdAt,
            completedAt: msg.createdAt,
          });
        }

        // 3. Synthesize step-evaluate from DB columns only if not already in trace.
        // The deal-generation family (Generate all / Regenerate estimate / Regenerate SOW)
        // is a deterministic multi-artifact pipeline, not an answer-producing Q&A, so a
        // "Confidence: X" self-review verdict is noise there - suppress it (matches the live
        // run, which never emits one for these skills).
        const isGenerationFamily = skillExecs.some((se) =>
          GENERATION_FAMILY_SKILL_KEYS.has(se.skillKey)
        );
        if (evalExec?.evaluationConfidence && !traceHasEvaluate && !isGenerationFamily) {
          const confLabel = `Confidence: ${evalExec.evaluationConfidence}`;
          const reasoningSummary = evalExec.evaluationReasoningSummary ?? undefined;
          const gaps = evalExec.evaluationGaps ?? [];
          steps.push({
            id: 'step-evaluate',
            type: 'generic_progress' as const,
            status: 'completed' as const,
            label: confLabel,
            detail: reasoningSummary,
            gaps,
            startedAt: msg.createdAt,
            completedAt: msg.createdAt,
          } as ExecutionStatus['steps'][number]);
        }

        // 4. (Removed) We no longer synthesize a generic "Generated response" row -
        // it added noise without information. A skill-emitted step-generate, if any,
        // is still rehydrated above from the trace steps.
      }

      // 5. Compute durationMs - prefer run-level metadata stored in agentTrace,
      //    fall back to span between first and last step timestamps. Span the whole
      //    turn: first execution's start → last execution's end (identical to the
      //    single-skill behaviour when there is exactly one execution row).
      const firstExecMeta = skillExecs[0]?.agentTrace as
        | { startedAt?: string; completedAt?: string; durationMs?: number }
        | null
        | undefined;
      const lastExecMeta = skillExecs[skillExecs.length - 1]?.agentTrace as
        | { startedAt?: string; completedAt?: string; durationMs?: number }
        | null
        | undefined;
      const firstStepStart = firstExecMeta?.startedAt ?? agentTraceSteps[0]?.startedAt;
      const lastStepEnd =
        lastExecMeta?.completedAt ??
        [...agentTraceSteps].reverse().find((ts) => ts.completedAt)?.completedAt;
      const durationMs =
        skillExecs.length === 1 && lastExecMeta?.durationMs != null
          ? lastExecMeta.durationMs
          : firstStepStart && lastStepEnd
            ? new Date(lastStepEnd).getTime() - new Date(firstStepStart).getTime()
            : undefined;

      const baseExecutionStatus: ExecutionStatus = {
        phase: isError ? 'failed' : isCancelled ? 'cancelled' : 'completed',
        steps,
        startedAt: firstStepStart ?? msg.createdAt,
        completedAt: lastStepEnd ?? msg.createdAt,
        durationMs,
      };
      // cancelExecution marks any in-progress steps as 'skipped' and clears activeStepId
      const executionStatus: ExecutionStatus = isCancelled
        ? cancelExecution(baseExecutionStatus, msg.createdAt)
        : baseExecutionStatus;

      const run: AssistantRun = {
        kind: 'assistant',
        runId: `hist-${msg.sessionId}-${msg.messageIndex}`,
        messageId: msg.uuid ?? null,
        lifecycleState: isError ? 'error' : isCancelled ? 'cancelled' : 'completed',
        model: (msg.modelName ?? '') as GeminiModel,
        mode: (msg.chatMode as ChatMode) ?? 'ask',
        // Error turns store the message as errorMessage, not as response text
        text: isError ? '' : stripLegacySections(msg.contentText),
        streaming: false,
        childEntries: [],
        // Ordered conversational transcript - when persisted, this flips ChatRunView
        // into segments mode so the run renders exactly as it was seen live (inline
        // tools + question/answer pairs). Absent → falls back to the text blob + chain.
        ...(msg.transcriptJson?.segments?.length
          ? { segments: msg.transcriptJson.segments }
          : {}),
        // Prefer the run's captured citation list (persisted on the transcript) - it keeps each
        // ref's real referenceId (source document uuid) and original index, so inline [N]
        // citations stay clickable and open the right source on reload. Fall back to the generic
        // citations subquery (RAG/codebase-QA turns, whose refs live in chat_message_citation).
        ragReferences:
          msg.transcriptJson?.ragReferences && msg.transcriptJson.ragReferences.length > 0
            ? msg.transcriptJson.ragReferences
            : citations.map(
                (c, idx): RagReference => ({
                  referenceId: `hist-${msg.sessionId}-${msg.messageIndex}-${idx}`,
                  sourceType:
                    c.artifactType === 'file'
                      ? 'repo_file'
                      : c.artifactType === 'page'
                        ? 'document_chunk'
                        : 'integration_record',
                  title: c.title,
                  snippet: c.citedExcerpt ?? undefined,
                  anchor: c.externalUrl ? { url: c.externalUrl } : undefined,
                  cited: true,
                  index: idx + 1,
                })
              ),
        executionStatus,
        startedAt: msg.createdAt,
        endedAt: msg.createdAt,
        // Error turns: surface the persisted message as the error box text
        ...(isError
          ? {
              errorMessage: msg.contentText,
              // Hydrate persisted error metadata so history matches live WS state.
              // errorCode: undefined when null (legacy rows or non-error turns).
              // retryable: defaults to true when null (legacy behaviour - show Retry button).
              ...(msg.errorCode != null ? { errorCode: msg.errorCode } : {}),
              retryable: msg.retryable ?? true,
            }
          : {}),
        // Rehydrate structured payload from content_json if present
        ...(msg.contentJson != null
          ? { structuredPayload: parseAiStructuredPayload(msg.contentJson) ?? undefined }
          : {}),
        // Rehydrate persisted action result.
        // action_result is one of:
        // - a plain string ('accepted'/'dismissed')
        // - JSON {"result":"accepted","fieldKeys":["f1","f2"]} (legacy single card)
        // - JSON {"cards":{"<cardKey>":{"result":...,"fieldKeys":[...]}}} (multi-card)
        // Parse JSON first; fall back to treating the raw value as the result string.
        ...(msg.actionResult != null
          ? (() => {
              let result: 'accepted' | 'dismissed' | null = null;
              let fieldKeys: string[] | undefined;
              try {
                const parsed = JSON.parse(msg.actionResult) as unknown;
                if (parsed !== null && typeof parsed === 'object' && 'cards' in parsed) {
                  const rawCards = (parsed as Record<string, unknown>).cards;
                  if (rawCards !== null && typeof rawCards === 'object') {
                    const cardActionResults: Record<string, AiStructuredCardActionState> = {};
                    for (const [key, entry] of Object.entries(
                      rawCards as Record<string, unknown>
                    )) {
                      if (entry === null || typeof entry !== 'object') continue;
                      const e = entry as Record<string, unknown>;
                      if (e.result !== 'accepted' && e.result !== 'dismissed') continue;
                      cardActionResults[key] = {
                        result: e.result,
                        ...(Array.isArray(e.fieldKeys)
                          ? {
                              fieldKeys: e.fieldKeys.filter(
                                (k): k is string => typeof k === 'string'
                              ),
                            }
                          : {}),
                      };
                    }
                    if (Object.keys(cardActionResults).length > 0) {
                      return { cardActionResults };
                    }
                  }
                  return {};
                }
                if (
                  parsed !== null &&
                  typeof parsed === 'object' &&
                  'result' in parsed &&
                  (parsed as Record<string, unknown>).result === 'accepted'
                ) {
                  result = 'accepted';
                  const keys = (parsed as Record<string, unknown>).fieldKeys;
                  if (Array.isArray(keys)) fieldKeys = keys as string[];
                } else if (
                  parsed !== null &&
                  typeof parsed === 'object' &&
                  'result' in parsed &&
                  (parsed as Record<string, unknown>).result === 'dismissed'
                ) {
                  result = 'dismissed';
                }
              } catch {
                // Plain string fallback
                if (msg.actionResult === 'accepted' || msg.actionResult === 'dismissed') {
                  result = msg.actionResult;
                }
              }
              if (!result) return {};
              return {
                actionResult: result,
                ...(fieldKeys ? { acceptedFieldKeys: fieldKeys } : {}),
              };
            })()
          : {}),
        // Rehydrate token usage so the context-usage indicator survives reloads
        ...(msg.tokenUsageInput != null
          ? {
              tokenUsage: {
                promptTokens: msg.tokenUsageInput,
                completionTokens: msg.tokenUsageOutput ?? 0,
                totalTokens: msg.tokenUsageInput + (msg.tokenUsageOutput ?? 0),
                // Persisted peak so the context-usage meter is accurate after reload
                // (falls back to promptTokens for legacy rows without it).
                ...(msg.tokenUsageContext != null
                  ? { contextTokens: msg.tokenUsageContext }
                  : {}),
              },
            }
          : {}),
        // Pass along the snapshot of the context captured at the time
        contextSnapshot: msg.contextSnapshotJson,
      };
      return [run];
    }
    return [];
  });
}
